import { sameValue } from '../../hooks/useDirtyForm';
import { CashDrawerPin, joinCashDrawer, splitCashDrawer } from './cashDrawer';
import type {
  DeliveryTicketOptions, PlatformKey, PreviewKey, PrintMethod, PrintingCategory, PrintingData, PrintingPrinter, PrintingRouting,
  RoutingRule, TicketStyle,
} from './printingApi';

/**
 * Κανόνες της σελίδας «Εκτυπωτές» χωρίς React: προεπιλογές/έλεγχοι της φόρμας εκτυπωτή, τι στέλνεται
 * στο PUT (μόνο ό,τι άλλαξε), το δέντρο κατηγοριών με την κληρονομιά της δρομολόγησης και τα σώματα
 * των PUT routing/switches/receipt-look.
 */

export const STATUS_REFRESH_MS = 30_000;
/** Η πρώτη έκδοση agent με μπιπ (cap 'beep'). Το συρτάρι: cashDrawer.agentSupportsCashDrawer. */
export const BEEP_MIN_AGENT = '2.3.1';

/** a.b.c ≥ min, αριθμητικά (2.10 > 2.9). Χωρίς έκδοση = παλιός agent. */
export function versionAtLeast(version: string | null | undefined, min: string): boolean {
  if (!version) return false;
  const a = version.split('.').map(x => parseInt(x, 10) || 0);
  const b = min.split('.').map(x => parseInt(x, 10) || 0);
  for (let i = 0; i < Math.max(a.length, b.length); i += 1) {
    const d = (a[i] || 0) - (b[i] || 0);
    if (d !== 0) return d > 0;
  }
  return true;
}

/* ───────────────────────── εκτυπωτές ───────────────────────── */

// Οι ανενεργοί (και οι γραμμές «(συγχωνεύθηκε)» της 272) δεν φαίνονται και δεν προσφέρονται πουθενά.
export const activePrinters = (list: PrintingPrinter[]): PrintingPrinter[] => list.filter(p => p.is_active !== false);
export const receiptPrinters = (list: PrintingPrinter[]): PrintingPrinter[] => activePrinters(list).filter(p => p.prints_receipts);
export const ticketPrinters = (list: PrintingPrinter[]): PrintingPrinter[] => activePrinters(list).filter(p => p.prints_tickets);

/**
 * Ο κύριος εκτυπωτής αποδείξεων όπως τον διαλέγει ο server (roles.pickMainReceiptPrinter): ο
 * «κύριος», αλλιώς ο πρώτος με τη σειρά του GET (ίδια σειρά με το PRINTER_ORDER).
 */
export const mainReceiptPrinter = (list: PrintingPrinter[]): PrintingPrinter | null => {
  const receipts = receiptPrinters(list);
  return receipts.find(p => p.is_default_receipt) || receipts[0] || null;
};

/**
 * Η φόρμα εκτυπωτή (απόφαση ιδιοκτήτη 25/10/2026): μέγεθος πακέτου, παύση ανάμεσα στα πακέτα, ύψος
 * λωρίδας και παλμοί συρταριού ΔΕΝ φαίνονται πια — οι τεχνικοί δεν τα άγγιζαν ποτέ. Οι τιμές τους
 * μένουν στη βάση όπως είναι (η φόρμα δεν τις στέλνει) και ο agent τις παίρνει όπως πριν.
 */
export interface PrinterDraft {
  name: string;
  windows_printer_name: string;
  prints_receipts: boolean;
  prints_tickets: boolean;
  ticket_style: TicketStyle;
  paper_width: 58 | 80;
  print_method: PrintMethod;
  /** Συρτάρι: «ανοίγει;» στην οθόνη· η ακίδα κρατιέται όπως ήταν (σχεδόν παντού 'both'). */
  drawer_enabled: boolean;
  drawer_pin: CashDrawerPin;
  beep_enabled: boolean;
}

export function draftFromPrinter(p: PrintingPrinter): PrinterDraft {
  const drawer = splitCashDrawer(p.cash_drawer);
  return {
    name: p.name || '',
    windows_printer_name: p.windows_printer_name || '',
    prints_receipts: p.prints_receipts,
    prints_tickets: p.prints_tickets,
    ticket_style: p.ticket_style === 'bar' ? 'bar' : 'kitchen',
    paper_width: Number(p.paper_width) === 58 ? 58 : 80,
    print_method: p.print_method,
    drawer_enabled: drawer.enabled,
    drawer_pin: drawer.pin,
    beep_enabled: p.beep_enabled === true,
  };
}

export interface PrinterPrefill {
  name?: string;
  windows_printer_name?: string;
}

/**
 * Νέος εκτυπωτής: 80 mm, «Εικόνα», συρτάρι στις αποδείξεις, χωρίς μπιπ (συμβόλαιο). Ρόλοι: ό,τι
 * λείπει από το κατάστημα — ο πρώτος εκτυπωτής κάνει και τα δύο· αν υπάρχουν ήδη και τα δύο,
 * κανένας προεπιλεγμένος και ο χρήστης διαλέγει.
 */
export function newDraft(printers: PrintingPrinter[], prefill: PrinterPrefill = {}): PrinterDraft {
  return {
    name: prefill.name ?? '',
    windows_printer_name: prefill.windows_printer_name ?? '',
    prints_receipts: receiptPrinters(printers).length === 0,
    prints_tickets: ticketPrinters(printers).length === 0,
    ticket_style: 'kitchen',
    paper_width: 80,
    print_method: 'image',
    drawer_enabled: true,
    drawer_pin: 'both',
    beep_enabled: false,
  };
}

export type DraftError = 'name' | 'windows_printer_name' | 'roles';

export function validateDraft(d: PrinterDraft): DraftError[] {
  const errors: DraftError[] = [];
  if (!d.name.trim()) errors.push('name');
  if (!d.windows_printer_name.trim()) errors.push('windows_printer_name');
  if (!d.prints_receipts && !d.prints_tickets) errors.push('roles');
  return errors;
}

/**
 * Το σώμα του εκτυπωτή όπως το βλέπει ο server. Συρτάρι μόνο σε εκτυπωτή αποδείξεων (στην κουζίνα
 * δεν υπάρχει) και μπιπ μόνο σε δελτία — αλλιώς «κλειστό».
 */
function body(d: PrinterDraft): Record<string, unknown> {
  const out: Record<string, unknown> = {
    name: d.name.trim(),
    windows_printer_name: d.windows_printer_name.trim(),
    prints_receipts: d.prints_receipts,
    prints_tickets: d.prints_tickets,
    paper_width: d.paper_width,
    print_method: d.print_method,
    cash_drawer: d.prints_receipts ? joinCashDrawer(d.drawer_enabled, d.drawer_pin) : 'off',
    beep_enabled: d.prints_tickets && d.beep_enabled,
  };
  if (d.prints_tickets) out.ticket_style = d.ticket_style;
  return out;
}

/** POST: όνομα, ουρά, ρόλοι, στυλ, χαρτί, μέθοδος, συρτάρι, μπιπ (+ κύριος, αν είναι ο πρώτος). */
export function createBody(d: PrinterDraft, printers: PrintingPrinter[]): Record<string, unknown> {
  const out = body(d);
  if (d.prints_receipts && receiptPrinters(printers).length === 0) out.is_default_receipt = true;
  return out;
}

/** PUT: μόνο ό,τι άλλαξε από τη γραμμή του server — ποτέ ολόκληρη η φόρμα. */
export function updateBody(initial: PrinterDraft, d: PrinterDraft): Record<string, unknown> {
  const before = body(initial);
  const after = body(d);
  const out: Record<string, unknown> = {};
  Object.keys(after).forEach(k => { if (!sameValue(before[k], after[k])) out[k] = after[k]; });
  return out;
}

/**
 * Το σώμα της «Δοκιμή ανοίγματος»: η ακίδα που δείχνει η φόρμα, πριν την αποθήκευση· οι διάρκειες του
 * παλμού λείπουν = ό,τι έχει ο εκτυπωτής στη βάση (routes/printers.js test-drawer).
 */
export function drawerTestBody(d: PrinterDraft): Record<string, unknown> {
  return { cash_drawer: joinCashDrawer(d.drawer_enabled, d.drawer_pin) };
}

/* ───────────────────────── «Τι πάει πού» ───────────────────────── */

export interface CategoryRow {
  id: number;
  name: string;
  depth: number;
  parentId: number | null;
  hidden: boolean;
  /** Η κρυφή «__uncategorized__» του καταστήματος: δείχνεται ως «Είδη χωρίς κατηγορία». */
  system: boolean;
}

/** Δέντρο κατηγοριών με τη σειρά του server: κάθε κατηγορία και αμέσως μετά οι υποκατηγορίες της. */
export function categoryRows(categories: PrintingCategory[]): CategoryRow[] {
  const ids = new Set(categories.map(c => c.id));
  const children = new Map<number | null, PrintingCategory[]>();
  categories.forEach(c => {
    const parent = c.parent_id != null && c.parent_id !== c.id && ids.has(c.parent_id) ? c.parent_id : null;
    const list = children.get(parent) || [];
    list.push(c);
    children.set(parent, list);
  });
  const out: CategoryRow[] = [];
  const seen = new Set<number>();
  const walk = (parent: number | null, depth: number) => {
    (children.get(parent) || []).forEach(c => {
      if (seen.has(c.id)) return;
      seen.add(c.id);
      out.push({ id: c.id, name: c.name, depth, parentId: parent, hidden: c.is_hidden === true, system: c.is_system === true });
      walk(c.id, depth + 1);
    });
  };
  walk(null, 0);
  // Κύκλος στο parent_id (δεν θα έπρεπε να υπάρχει): ό,τι έμεινε, ως κατηγορία χωρίς γονέα.
  categories.forEach(c => {
    if (seen.has(c.id)) return;
    seen.add(c.id);
    out.push({ id: c.id, name: c.name, depth: 0, parentId: null, hidden: c.is_hidden === true, system: c.is_system === true });
  });
  return out;
}

/** Τιμή γραμμής: '' = χωρίς δικό της κανόνα, '12' = ένας εκτυπωτής, '12,15' = σε δύο (παλιός κανόνας). */
export const targetIds = (value: string | undefined | null): number[] =>
  (value ? value.split(',').map(Number).filter(n => Number.isFinite(n)) : []);

export interface Effective {
  ids: number[];
  /** own = δικός της κανόνας, parent = της κατηγορίας (ή παραπάνω), unrouted = «Είδη χωρίς κανόνα». */
  source: 'own' | 'parent' | 'unrouted' | 'none';
}

/** Πού βγαίνει το δελτίο μιας κατηγορίας: δικός κανόνας → της πλησιέστερης γονικής → «χωρίς κανόνα». */
export function effectiveTarget(
  row: CategoryRow,
  byId: Map<number, CategoryRow>,
  values: Record<string, string>,
  unrouted: number | null,
): Effective {
  const own = targetIds(values[String(row.id)]);
  if (own.length > 0) return { ids: own, source: 'own' };
  return inheritedTarget(row, byId, values, unrouted);
}

/** Ό,τι θα ίσχυε χωρίς δικό της κανόνα (η επιλογή «όπως η κατηγορία» / «χωρίς κανόνα»). */
export function inheritedTarget(
  row: CategoryRow,
  byId: Map<number, CategoryRow>,
  values: Record<string, string>,
  unrouted: number | null,
): Effective {
  const seen = new Set<number>([row.id]);
  let parent = row.parentId;
  while (parent != null && !seen.has(parent)) {
    seen.add(parent);
    const ids = targetIds(values[String(parent)]);
    if (ids.length > 0) return { ids, source: 'parent' };
    parent = byId.get(parent)?.parentId ?? null;
  }
  return unrouted != null ? { ids: [unrouted], source: 'unrouted' } : { ids: [], source: 'none' };
}

export interface PageForm {
  /** Ανά κατηγορία (String(id)), μία για κάθε κατηγορία· το PUT ακολουθεί τη σειρά του δέντρου (rows). */
  rules: Record<string, string>;
  /**
   * «Δελτία ανά κατηγορία προϊόντων» (απόφαση ιδιοκτήτη 25/10/2026): κανονικός διακόπτης. Κλειστός = ο
   * πίνακας δεν ισχύει και η αποθήκευση ΣΒΗΝΕΙ τους κανόνες — κάθε δελτίο βγαίνει ολόκληρο στον κύριο
   * εκτυπωτή δελτίων (ή στον εκτυπωτή του τύπου παραγγελίας). Ανοίγει μόνος του όπου υπάρχουν κανόνες.
   */
  byCategory: boolean;
  /** Κύριος εκτυπωτής δελτίων (= «Είδη χωρίς κανόνα»): '' = κανένα δελτίο, αλλιώς String(printer id). */
  unrouted: string;
  delivery: string;
  /**
   * Take Away / Τραπέζια (274): '' = «Όπως οι κατηγορίες» (ο πίνακας, ή χωρίς πίνακα ο κύριος εκτυπωτής
   * δελτίων)· String(id) = ΟΛΗ η παραγγελία σε ένα δελτίο σε αυτόν τον εκτυπωτή, χωρίς τους κανόνες.
   */
  takeaway: string;
  dinein: string;
  /** Παραστατικά delivery & πλατφορμών (274): '' = ο κύριος εκτυπωτής αποδείξεων, αλλιώς String(id). */
  receiptsDelivery: string;
  /** Κύριος εκτυπωτής αποδείξεων (printers.is_default): String(id)· '' μόνο χωρίς εκτυπωτή αποδείξεων. */
  mainReceipt: string;
  /** Περιοχή τραπεζιών (String(zone id)) → '' = ο κύριος, αλλιώς String(printer id) — ίδιο με την κάτοψη. */
  zones: Record<string, string>;
  takeaway_ticket: boolean;
  dinein_kitchen_ticket: boolean;
  efood: boolean | null;
  wolt: boolean | null;
  box: boolean | null;
  /** «Έντονα γράμματα στα δελτία» (276)· null = ο server δεν το έχει → ούτε φαίνεται ούτε στέλνεται. */
  ticket_bold: boolean | null;
  logo_url: string | null;
  header_text: string;
  /** «Κείμενο στο τέλος της απόδειξης» (280) — ένα πεδίο, πολλές γραμμές. */
  thank_you_text: string;
  /** Επιλογές δελτίου delivery (280)· null = ο server δεν τις έχει → ούτε φαίνονται ούτε στέλνονται. */
  deliveryTicket: DeliveryTicketOptions | null;
}

// Όλα όσα σώζει το PUT /printing/routing: αλλαγή σε οποιοδήποτε = ένα PUT με όλη τη δρομολόγηση.
export const ROUTING_KEYS: Array<keyof PageForm> = [
  'rules', 'byCategory', 'unrouted', 'delivery', 'takeaway', 'dinein', 'receiptsDelivery', 'mainReceipt', 'zones',
];
export const SWITCH_KEYS: Array<keyof PageForm> = ['takeaway_ticket', 'dinein_kitchen_ticket', 'efood', 'wolt', 'box', 'ticket_bold'];
export const LOOK_KEYS: Array<keyof PageForm> = ['logo_url', 'header_text', 'thank_you_text'];
export const TICKET_OPTION_KEYS: Array<keyof PageForm> = ['deliveryTicket'];

export const EMPTY_FORM: PageForm = {
  rules: {}, byCategory: false, unrouted: '', delivery: '', takeaway: '', dinein: '', receiptsDelivery: '', mainReceipt: '', zones: {},
  takeaway_ticket: true, dinein_kitchen_ticket: true, efood: null, wolt: null, box: null, ticket_bold: null,
  logo_url: null, header_text: '', thank_you_text: '', deliveryTicket: null,
};

const idText = (id: number | null | undefined): string => (id == null ? '' : String(id));
const idOf = (value: string): number | null => (value === '' ? null : Number(value));

/** Οι τρεις στόχοι ανά είδος παραγγελίας (274) — πεδία της φόρμας. */
export type OrderTargetKey = 'takeaway' | 'dinein' | 'receiptsDelivery';

/** Ποια στήλη του stores λέει κάθε warning του server (routes/printing.js). */
const WARNING_KEY: Record<string, OrderTargetKey> = {
  tickets_takeaway_printer_id: 'takeaway',
  tickets_dinein_printer_id: 'dinein',
  receipts_delivery_printer_id: 'receiptsDelivery',
};

/** Αποθηκευμένος στόχος που ο server δεν εφαρμόζει (ο εκτυπωτής έχασε τον ρόλο): η φόρμα δείχνει την προεπιλογή. */
export interface InactiveTarget {
  key: OrderTargetKey;
  printerId: number;
}

/** Ό,τι λέει ο server για τους στόχους ανά είδος παραγγελίας — όχι τιμές της φόρμας. */
export interface OrderTargetsInfo {
  /** false = server πριν από τα πεδία: οι επιλογές δεν φαίνονται και η σελίδα μένει όπως ήταν. */
  supported: boolean;
  inactive: InactiveTarget[];
}

export const NO_ORDER_TARGETS: OrderTargetsInfo = { supported: false, inactive: [] };

export function orderTargetsInfo(routing: PrintingRouting): OrderTargetsInfo {
  const inactive: InactiveTarget[] = [];
  routing.warnings.forEach(w => {
    const key = w.code === 'target_inactive' && w.key ? WARNING_KEY[w.key] : undefined;
    if (key && w.printer_id != null && !inactive.some(x => x.key === key)) inactive.push({ key, printerId: w.printer_id });
  });
  return { supported: routing.order_targets, inactive };
}

/**
 * Φόρμα από την απάντηση του server. `passthrough`: κανόνες για κατηγορία που δεν είναι στη λίστα —
 * δεν φαίνονται, ξαναστέλνονται όπως ήρθαν (το PUT αντικαθιστά όλους τους κανόνες). Κανόνες χωρίς
 * κατηγορία δεν ταίριαζαν ποτέ σε είδος (printService.groupItemsByPrinter) και αντικαθίστανται από
 * τα «Είδη χωρίς κανόνα»: δεν ξαναστέλνονται. Ο διακόπτης «ανά κατηγορία» ανοιχτός όπου υπάρχει κανόνας.
 */
export function formFrom(data: PrintingData, rows: CategoryRow[]): { form: PageForm; passthrough: RoutingRule[] } {
  const known = new Set(rows.map(r => r.id));
  const byCategory = new Map<number, number[]>();
  const passthrough: RoutingRule[] = [];
  data.routing.rules.forEach(rule => {
    if (rule.category_id == null) return;
    if (!known.has(rule.category_id)) {
      passthrough.push(rule);
      return;
    }
    const list = byCategory.get(rule.category_id) || [];
    if (!list.includes(rule.printer_id)) list.push(rule.printer_id);
    byCategory.set(rule.category_id, list);
  });
  const rules: Record<string, string> = {};
  rows.forEach(r => { rules[String(r.id)] = (byCategory.get(r.id) || []).join(','); });
  const zones: Record<string, string> = {};
  data.routing.zones.forEach(z => { zones[String(z.id)] = idText(z.printer_id); });
  const { switches, receipt_look: look } = data;
  return {
    passthrough,
    form: {
      rules,
      byCategory: byCategory.size > 0 || passthrough.length > 0,
      unrouted: idText(data.routing.unrouted_printer_id),
      delivery: idText(data.routing.delivery_printer_id),
      takeaway: idText(data.routing.takeaway_printer_id),
      dinein: idText(data.routing.dinein_printer_id),
      receiptsDelivery: idText(data.routing.receipts_delivery_printer_id),
      mainReceipt: idText(mainReceiptPrinter(data.printers)?.id),
      zones,
      takeaway_ticket: switches.takeaway_ticket,
      dinein_kitchen_ticket: switches.dinein_kitchen_ticket,
      efood: switches.platforms.efood,
      wolt: switches.platforms.wolt,
      box: switches.platforms.box,
      ticket_bold: switches.ticket_bold,
      logo_url: look.logo_url,
      header_text: look.header_text || '',
      thank_you_text: look.thank_you_text || '',
      deliveryTicket: data.ticket_options ? { ...data.ticket_options.delivery } : null,
    },
  };
}

/** Πόσες κατηγορίες έχουν δικό τους κανόνα (αυτοί σβήνονται αν κλείσει ο διακόπτης). */
export const ruleCount = (form: PageForm, passthrough: RoutingRule[] = []): number =>
  Object.values(form.rules).filter(v => targetIds(v).length > 0).length + passthrough.length;

/**
 * Όλη η δρομολόγηση, όπως τη δείχνει η φόρμα: κανόνες (κανένας με κλειστό «ανά κατηγορία») + οι
 * πέντε στόχοι (null = προεπιλογή). Και οι κρυφοί στόχοι (κατάστημα χωρίς Take Away/τραπέζια)
 * ξαναστέλνονται όπως ήρθαν. Ο κύριος εκτυπωτής αποδείξεων και οι περιοχές ΜΟΝΟ όταν άλλαξαν (σε
 * σχέση με το `saved`): αλλιώς μια παλιά τιμή της φόρμας θα γύριζε πίσω αλλαγή που έγινε στο μεταξύ
 * από τη φόρμα του εκτυπωτή ή από την κάτοψη. Παλιός server αγνοεί ό,τι δεν ξέρει.
 */
export function routingBody(rows: CategoryRow[], form: PageForm, passthrough: RoutingRule[], saved: PageForm = form): Record<string, unknown> {
  const rules: RoutingRule[] = [];
  if (form.byCategory) {
    rows.forEach(r => targetIds(form.rules[String(r.id)]).forEach(printerId => rules.push({ category_id: r.id, printer_id: printerId })));
    rules.push(...passthrough);
  }
  const out: Record<string, unknown> = {
    rules,
    unrouted_printer_id: idOf(form.unrouted),
    delivery_printer_id: idOf(form.delivery),
    takeaway_printer_id: idOf(form.takeaway),
    dinein_printer_id: idOf(form.dinein),
    receipts_delivery_printer_id: idOf(form.receiptsDelivery),
  };
  if (form.mainReceipt !== '' && form.mainReceipt !== saved.mainReceipt) out.main_receipt_printer_id = Number(form.mainReceipt);
  const zones = Object.keys(form.zones)
    .filter(id => (form.zones[id] || '') !== (saved.zones[id] || ''))
    .map(id => ({ id: Number(id), printer_id: idOf(form.zones[id] || '') }));
  if (zones.length > 0) out.zones = zones;
  return out;
}

/** Μόνο οι διακόπτες που άλλαξαν· πλατφόρμα μη ενεργή (null) δεν στέλνεται ποτέ. */
export function switchesBody(changed: Array<keyof PageForm>, form: PageForm): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (changed.includes('takeaway_ticket')) out.takeaway_ticket = form.takeaway_ticket;
  if (changed.includes('dinein_kitchen_ticket')) out.dinein_kitchen_ticket = form.dinein_kitchen_ticket;
  const platforms: Partial<Record<PlatformKey, boolean>> = {};
  (['efood', 'wolt', 'box'] as PlatformKey[]).forEach(key => {
    const value = form[key];
    if (changed.includes(key) && value !== null) platforms[key] = value;
  });
  if (Object.keys(platforms).length > 0) out.platforms = platforms;
  if (changed.includes('ticket_bold') && form.ticket_bold !== null) out.ticket_bold = form.ticket_bold;
  return out;
}

/** Μόνο ό,τι άλλαξε· κενό κείμενο = null = ΚΑΜΙΑ γραμμή στην απόδειξη (ο server τυπώνει ακριβώς ό,τι γράφεται). */
export function lookBody(changed: Array<keyof PageForm>, form: PageForm): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (changed.includes('logo_url')) out.logo_url = form.logo_url;
  if (changed.includes('header_text')) out.header_text = form.header_text.trim() || null;
  if (changed.includes('thank_you_text')) out.thank_you_text = form.thank_you_text.trim() || null;
  return out;
}

/** Οι επιλογές δελτίου delivery, όλες, όταν άλλαξαν· server χωρίς επιλογές (null) = τίποτα. */
export function ticketOptionsBody(changed: Array<keyof PageForm>, form: PageForm): Record<string, unknown> {
  return changed.includes('deliveryTicket') && form.deliveryTicket ? { delivery: { ...form.deliveryTicket } } : {};
}

/** Οι ενεργοί εκτυπωτές ανά ρόλο, μετά από αλλαγή εκτυπωτών (keepPrinters). */
export interface AllowedPrinters {
  tickets: Set<number>;
  receipts: Set<number>;
}

/**
 * Μετά από αλλαγή εκτυπωτών με φόρμα που έχει αλλαγές: κανόνες και στόχοι δελτίων μόνο σε ενεργούς
 * εκτυπωτές δελτίων, τα παραστατικά delivery / ο κύριος / οι περιοχές μόνο σε ενεργό εκτυπωτή αποδείξεων.
 * Ο server σβήνει ήδη τους κανόνες/στόχους εκτυπωτή που διαγράφηκε ή έχασε τον ρόλο (και θα απέρριπτε το
 * PUT τους), άρα η φόρμα δεν τους ξαναστέλνει — ο στόχος γυρίζει στην προεπιλογή του. Ο κύριος που
 * χάθηκε γίνεται ό,τι διάλεξε ο server (`fallbackMain`).
 */
export function keepPrinters(form: PageForm, allowed: AllowedPrinters, fallbackMain = ''): PageForm {
  const keep = (value: string, ids: Set<number>) => targetIds(value).filter(id => ids.has(id)).join(',');
  const rules: Record<string, string> = {};
  Object.keys(form.rules).forEach(k => { rules[k] = keep(form.rules[k], allowed.tickets); });
  const zones: Record<string, string> = {};
  Object.keys(form.zones).forEach(k => { zones[k] = keep(form.zones[k], allowed.receipts); });
  return {
    ...form,
    rules,
    unrouted: keep(form.unrouted, allowed.tickets),
    delivery: keep(form.delivery, allowed.tickets),
    takeaway: keep(form.takeaway, allowed.tickets),
    dinein: keep(form.dinein, allowed.tickets),
    receiptsDelivery: keep(form.receiptsDelivery, allowed.receipts),
    mainReceipt: keep(form.mainReceipt, allowed.receipts) || fallbackMain,
    zones,
  };
}

/** Όρια του PUT receipt-look (routes/printing.js LOOK_LIMITS). */
export const HEADER_MAX = 200;
export const HEADER_LINES_MAX = 3;
export const END_TEXT_MAX = 500;
export const END_TEXT_LINES_MAX = 8;
export const textLineCount = (text: string): number => (text.trim() === '' ? 0 : text.trim().split('\n').length);
/** Κεφαλίδα και κείμενο τέλους μέσα στα όρια γραμμών του server — αλλιώς η μπάρα δεν αποθηκεύει. */
export const lookLinesOk = (form: PageForm): boolean =>
  textLineCount(form.header_text) <= HEADER_LINES_MAX && textLineCount(form.thank_you_text) <= END_TEXT_LINES_MAX;

/* ───────────────────────── προεπισκόπηση ───────────────────────── */

const widthOf = (printers: PrintingPrinter[], value: string): 58 | 80 | null => {
  const id = targetIds(value)[0];
  const p = id != null ? printers.find(x => x.id === id) : undefined;
  return p ? (p.paper_width === 58 ? 58 : 80) : null;
};

/**
 * Το χαρτί κάθε προεπισκόπησης: του εκτυπωτή που θα το τυπώσει με ό,τι δείχνει η φόρμα — απόδειξη στον
 * κύριο, Take Away/τραπέζια στον δικό τους ή στον κύριο εκτυπωτή δελτίων, delivery στον δικό του.
 */
export function previewPaper(printers: PrintingPrinter[], form: PageForm): Record<PreviewKey, 58 | 80> {
  const ticket = (own: string) => widthOf(printers, own) ?? widthOf(printers, form.unrouted) ?? 80;
  return {
    receipt: widthOf(printers, form.mainReceipt) ?? 80,
    takeaway: ticket(form.takeaway),
    dinein: ticket(form.dinein),
    delivery: widthOf(printers, form.delivery) ?? widthOf(printers, form.unrouted) ?? 80,
  };
}
